import Navbar from '../components/Navbar';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Package, Ship, CreditCard, TestTube, HardHat, Shield, HelpCircle } from 'lucide-react';

function FAQ() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="faq-page">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                Help Center
              </span>
            </div>
            <h1 className="font-bold text-4xl md:text-6xl tracking-tight leading-tight mb-6" data-testid="faq-title">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about ordering, shipping, and receiving premium building materials through BlueCrate.
            </p>
          </div>

          {/* Ordering & Pledging */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <Package className="w-6 h-6 text-primary mr-3" />
              <h2 className="text-2xl font-bold">Ordering & Pledging</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  How do I place an order?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Developers join a container by pledging the quantity of material they need.
                  Once you submit your pledge, our team confirms your pricing and assigns you to the next available container.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  What is a pledge?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  A pledge is your commitment to purchase a minimum of 1,500 sq ft of flooring material through our shared container model. When you place a pledge, you pay <strong>30% deposit</strong> to secure your order. The remaining <strong>70% is due once your flooring is ready to ship</strong>. Production begins when the container reaches 80% capacity.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  Is there a minimum order size?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Yes — BlueCrate uses a 1,500 sq ft minimum pledge per customer for flooring.
                  This ensures fair allocation and maximum discounts.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  Can I mix product types in one pledge?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Yes, depending on the container type.
                  Some containers are dedicated to vinyl, some to engineered hardwood, and others to doors or cabinets.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Shipping & Delivery */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <Ship className="w-6 h-6 text-primary mr-3" />
              <h2 className="text-2xl font-bold">Shipping & Delivery</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-5" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  How long does shipping take?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Shipping from China to our U.S. warehouse typically takes 18–30 days depending on weather and port traffic.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  How long do I wait for a container to fill?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  <strong>Average fill time:</strong> 2–4 weeks<br />
                  <strong>Maximum recommended wait time:</strong> 6–8 weeks<br /><br />
                  We provide real-time container progress so you always know where things stand.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  Where do materials get delivered?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  We offer two delivery options:<br />
                  • Pickup from our Iowa warehouse<br />
                  • Direct delivery to your jobsite (additional fee based on mileage)
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  Will I get tracking information?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Yes. Once your container departs, you receive:<br />
                  • Bill of Lading<br />
                  • Container number<br />
                  • ETA to port<br />
                  • ETA to warehouse<br />
                  • Pickup/delivery scheduling options
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Payment & Deposit */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <CreditCard className="w-6 h-6 text-primary mr-3" />
              <h2 className="text-2xl font-bold">Payment Structure</h2>
            </div>
            
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-3xl p-8 border-2 border-primary/20 mb-6">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <CreditCard className="w-5 h-5 text-primary mr-2" />
                30% Deposit / 70% on Production
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                To keep our pricing low and guarantee your spot, we require <strong>30% deposit</strong> when you submit a pledge.
              </p>
              <div className="space-y-2 text-gray-700">
                <p>This payment:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Confirms your place in the current container</li>
                  <li>Locks in your price</li>
                  <li>Ensures serious participation</li>
                  <li>Allows us to coordinate production and shipping timelines</li>
                </ul>
              </div>
              <p className="text-gray-700 leading-relaxed mt-4">
                Your remaining balance (<strong>70%</strong>) is due once your flooring production is complete and ready to ship.
              </p>
              <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200">
                <p className="text-sm text-green-800">
                  <strong>Production starts</strong> when the container reaches 80% capacity, OR you can choose "Ship Now" for immediate production (100% payment required).
                </p>
              </div>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-9" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  What payment methods do you accept?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  • Bank transfer (ACH)<br />
                  • Wire<br />
                  • Credit card (3% service fee applies)
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-10" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  Can I pay in full upfront?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Yes! You can choose to pay 100% upfront at checkout. This is required if you select the "Ship Now" option for immediate production and shipping.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-11" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  Do you offer financing?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Yes — we partner with local banks to offer:<br />
                  • Credit lines<br />
                  • Company cards<br />
                  • Project-based financing<br /><br />
                  Ask us for details.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Sample Kits */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <TestTube className="w-6 h-6 text-primary mr-3" />
              <h2 className="text-2xl font-bold">Sample Kits</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-11" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  Can I order samples first?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Yes. We offer a full BlueCrate Sample Box, delivered to your address.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-12" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  How long do samples take to arrive?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Typically 3–5 business days for U.S. customers.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-13" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  Are samples free?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Elite Members receive free sample kits.<br />
                  Basic and Pro Members pay a standard shipping fee.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Builders & Developers */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <HardHat className="w-6 h-6 text-primary mr-3" />
              <h2 className="text-2xl font-bold">Builders & Developers</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-14" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  Can I order materials for multiple projects?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Yes — simply submit separate pledges for each project.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-15" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  Can I buy out an entire container?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Yes, if available.<br />
                  This is the fastest method and guarantees full control over the shipment.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-16" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  Can BlueCrate source custom products?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Yes — Elite Members can request:<br />
                  • Private-label flooring<br />
                  • Custom colors<br />
                  • Custom door designs<br />
                  • Pre-assembled cabinets<br /><br />
                  We work directly with factories to fulfill specialty orders.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Returns & Quality */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <Shield className="w-6 h-6 text-primary mr-3" />
              <h2 className="text-2xl font-bold">Returns & Quality Assurance</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-17" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  Do you accept returns?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Due to the nature of international shipping, we do not accept returns unless:<br />
                  • There is a manufacturing defect<br />
                  • The product arrived damaged<br />
                  • The wrong product was delivered
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-18" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  What if my materials arrive damaged?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  We resolve this immediately by:<br />
                  1. Documenting the issue<br />
                  2. Contacting the factory<br />
                  3. Crediting or replacing the material<br /><br />
                  BlueCrate handles all manufacturer conversations on your behalf.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-19" className="bg-white rounded-2xl border border-gray-100 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  What certifications do your suppliers have?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  Our products meet or exceed:<br />
                  • ISO safety standards<br />
                  • International environmental certifications<br />
                  • U.S. flooring safety requirements (e.g., formaldehyde limits)
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Support */}
          <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-3xl p-8 md:p-12 border-2 border-primary/20 text-center">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Still need help?</h2>
            <a 
              href="mailto:hello@bluecrategroup.com" 
              className="text-2xl font-bold text-primary hover:text-primary/80 transition-colors block mb-2"
            >
              hello@bluecrategroup.com
            </a>
            <p className="text-gray-600">We respond within 24 hours Monday–Friday.</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <img src="/bluecrate-logo.jpg" alt="BlueCrate" className="h-10" />
          </div>
          <p className="text-sm text-gray-600">
            © 2025 BlueCrate Group. Premium building materials through cooperative shipping.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default FAQ;
